import { ReactElement, ReactNode, Suspense, useEffect, useState } from "react";
import { AuthContextProps, useAuth } from "react-oidc-context";
import { startLogin, useSession } from "app/hooks/useSession";
import { UserInfoModel } from "app/model/ua/user-info";
import { AuthenticationService } from "app/services/common/AuthenticationService";
import { HttpStatusCode } from "axios";
import { clearUserInfo, setUserInfo } from "app/reducers/userSlice";
import { spinner } from "App";
import { useAppDispatch, useAppSelector } from "app/store/hook";

interface AppProtectorProps {
  children: ReactNode;
}
export default function AuthProtector(props: AppProtectorProps): ReactElement {
  const { children } = props;
  const dispatch = useAppDispatch();
  const auth: AuthContextProps = useAuth();
  // 로그인 여부는 방식에 따라 판단이 다르다. BFF 에서는 화면에 토큰이 없어
  // 기존 판단(토큰 유무)이 항상 '미로그인'이 된다.
  const session = useSession();
  const userInfo: UserInfoModel = useAppSelector((state) => state.userInfo.userInfo);

  const fetchCurrentUserInfo = () => {
    let newUserInfo: UserInfoModel | null = null;
    try {
      AuthenticationService.getInstance().getUserInfo()
      .then((response) => {
        if (response && response.data && response.statusCode === HttpStatusCode.Ok) {
          newUserInfo = new UserInfoModel(response.data);
        } else {
          newUserInfo = null;
        }
      })
      .catch(() => {
        newUserInfo = null;
      })
      .finally(() => {
        if (newUserInfo) {
          dispatch(setUserInfo(newUserInfo));
        } else {
          dispatch(clearUserInfo());
        }
      });
    } catch {
      dispatch(clearUserInfo());
    }
  };

  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  /**
   * 로그인으로 보내지 못한 상태.
   *
   * 방금 로그인에 실패해 돌아온 경우 다시 보내지 않는다(무한 반복 방지). 그때 아무것도
   * 하지 않으면 사용자가 안내 없는 빈 화면(스피너)에 남는다.
   */
  const [loginBlocked, setLoginBlocked] = useState<boolean>(false);

  useEffect(() => {
    setIsAuthorized(false);
    if (session.isLoading) {
      // 아직 판단 중이다. 여기서 로그인으로 보내면 이미 로그인한 사용자도 튕긴다.
      return;
    }

    if (!session.isAuthenticated) {
      setLoginBlocked(!startLogin(auth));
      return;
    }
    setLoginBlocked(false);

    if (userInfo && userInfo.userId && session.userId === userInfo.userId) {
      setIsAuthorized(true);
    } else {
      fetchCurrentUserInfo();
    }
  }, [auth, session, userInfo]);


  if (loginBlocked) {
    // 스피너를 계속 돌리면 사용자는 무엇을 기다리는지 알 수 없다.
    return (
      <div style={{ padding: '4rem', textAlign: 'center' }}>
        <p>로그인이 완료되지 않았습니다.</p>
        <button type="button" onClick={() => window.location.assign('/portal/')}>
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <>
      {isAuthorized ? children : <Suspense fallback={spinner} /> }
    </>
  )
}
